<script setup lang="ts">
/**
 * The Cart Nav
 * --------------------------------
 *
 * Cart Icon in the header
 *
 */

const localeRoute = useLocaleRoute();
const { openConfirmDialog } = useConfirmActionDialog();
const { t: $t } = useNuxtApp().$i18n;
const cartStore = useCartStore();
const route = useRoute();

const routeName = computed(() => getRouteName(route) ?? '');
const isCheckout = computed(() => routeName.value.startsWith('checkout'));
const isOfferFlow = computed(() => routeName.value.startsWith('offerFlow'));
const isIndex = computed(() => routeName.value.startsWith('index'));
// Show everyhwere but esav, if there are > 0 items in cart
const isVisible = computed(() => cartStore.numberOfCartItems > 0 && (isOfferFlow.value || isCheckout.value || isIndex.value));

/**
 * Click cart icon
 */
function clickCart() {
    let isProductUntouched = false;
    let isEditing = false;
    try {
        isProductUntouched = isOfferFlow.value && !useProduct().product.value?.status.isTouched;
        isEditing = isOfferFlow.value && cartStore.products.some(p => p.id === useProduct().product.value?.id);
    }
    catch (error) {
        console.error(error);
    }

    // If there are no changes to the edited product or we are already in checkout, navigate directly
    if (isProductUntouched || isCheckout.value || isIndex.value) {
        navigateTo(localeRoute({ name: 'checkout-checkout-1-cart' }));
    }
    // If editing an existing product and something changed already,
    // show confirm dialog, because going back to the cart without saving,
    // the changes would be lost
    else if (isEditing) {
        openConfirmDialog(
            $t('offerFlow.cartNav.confirmDialog.editing.heading'),
            $t('offerFlow.cartNav.confirmDialog.editing.body'),
            () => {
                navigateTo(localeRoute({ name: 'checkout-checkout-1-cart' }));
            },
            $t('offerFlow.cartNav.confirmDialog.editing.confirmLabel'),
            () => {},
            $t('offerFlow.cartNav.confirmDialog.editing.cancelLabel'),
        );
    }
    // For a new product in the offer flow, show confirm dialog aswell, because going back to the cart
    // would lose the current product, as it's not yet finished and added to cart.
    else {
        openConfirmDialog(
            $t('offerFlow.cartNav.confirmDialog.new.heading'),
            $t('offerFlow.cartNav.confirmDialog.new.body'),
            () => {
                navigateTo(localeRoute({ name: 'checkout-checkout-1-cart' }));
            },
            $t('offerFlow.cartNav.confirmDialog.new.confirmLabel'),
            () => {},
            $t('offerFlow.cartNav.confirmDialog.new.cancelLabel'),
        );
    }
}
</script>

<template>
    <button
        v-if="isVisible"
        class="relative size-7 print:hidden"
        @click="clickCart"
    >
        <span class="sr-only">
            {{ $t('core.buttons.toCart') }}
        </span>
        <span class="ml abs-center-x top-[2px] ml-[2px] typo-meta-s-bold">
            {{ cartStore.numberOfCartItems }}
        </span>
        <Icon name="tot:cart" class="abs-center-x bottom-0 size-7" />
    </button>
</template>
